import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { decrementQueriesRemaining } from '../../store/slices/userSlice';
import { CText, CLoader, SEO } from '../../components/common';
import { IoMenu, IoSparkles, IoAdd, IoStop } from 'react-icons/io5';
import {
  queryStockResearch,
  getAllCompanyNames,
  fetchCopilotModels,
  ResearchQuery,
  ResearchResult,
  CopilotModelOption,
} from '../../api/copilot';
import {
  getConversations,
  getConversation,
  createConversation,
  deleteConversation,
  addMessage,
  checkQueryLimit,
  submitFeedback,
  Conversation,
  Message,
  Source,
} from '../../api/chat';
import { useSubscriptionGate } from '../../hooks/useSubscriptionGate';
import ChatSidebar from './components/ChatSidebar';
import ChatMessage from './components/ChatMessage';
import ChatInput, { ChatOptions } from './components/ChatInput';

// Local extension — only used in-memory, not persisted
interface LocalMessage extends Message {
  pendingId?: string;
  loading?: boolean;
  llm_response_en?: string;
  response_language?: string;
}

const Copilot: React.FC = () => {
  const colors = useAppSelector((state) => state.theme.theme);
  const authToken = useAppSelector((state) => state.user.authToken);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const subscriptionTier = useAppSelector((state) => state.user.subscription.tier);
  const queriesRemaining = useAppSelector((state) => state.user.subscription.queriesRemaining);
  const deepSearchGate = useSubscriptionGate('deep_search');
  const [queryLimitHit, setQueryLimitHit] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const qProcessedRef = useRef(false);

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<number | null>(null);

  // Chat state
  const [messages, setMessages] = useState<LocalMessage[]>([]);
  const [pendingMessageIds, setPendingMessageIds] = useState<Set<string>>(new Set());
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [pollingConvId, setPollingConvId] = useState<number | null>(null);

  // Derived loading boolean for any checks that need it
  const loading = pendingMessageIds.size > 0;

  // Helper: check if messages end with a user message (no assistant response yet)
  const hasDanglingUserMessage = (msgs: Message[]) =>
    msgs.length > 0 && msgs[msgs.length - 1].role === 'user';

  // Ref to prevent race condition when creating conversation from concurrent queries
  const conversationCreatingRef = useRef<Promise<number> | null>(null);

  // Track whether we've auto-selected a conversation (avoids dependency on currentConversationId)
  const hasAutoSelectedRef = useRef(false);

  // Abort controllers for pending queries
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());

  // Edit state
  const [editingMessageIndex, setEditingMessageIndex] = useState<number | null>(null);

  // Feedback state: messageId → {rating, comment}
  const [feedbackMap, setFeedbackMap] = useState<Record<number, { rating: number; comment?: string }>>({});

  // Company selection state
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [allCompanies, setAllCompanies] = useState<string[]>([]);

  // Dynamic model options from API
  const [modelOptions, setModelOptions] = useState<CopilotModelOption[]>([]);

  // Language state (persisted in localStorage)
  const [copilotLanguage, setCopilotLanguage] = useState<string>(
    () => localStorage.getItem('copilot_language') || 'en-IN'
  );
  const handleLanguageChange = (lang: string) => {
    setCopilotLanguage(lang);
    localStorage.setItem('copilot_language', lang);
  };

  // Load companies and model options on mount
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const companies = await getAllCompanyNames();
        setAllCompanies(companies);
      } catch (err) {
        console.error('Failed to load companies:', err);
      }
    };
    const loadModels = async () => {
      try {
        const models = await fetchCopilotModels();
        setModelOptions(models.map(m => ({ key: m.key, label: m.label, description: m.description })));
      } catch (err) {
        console.error('Failed to load copilot models:', err);
      }
    };
    loadCompanies();
    loadModels();
  }, []);

  // Load conversations on mount
  useEffect(() => {
    const loadConversations = async () => {
      if (!authToken) {
        setConversationsLoading(false);
        return;
      }
      try {
        const convs = await getConversations(authToken);
        setConversations(convs);
        // Auto-select the most recent conversation so users see their last chat
        if (convs.length > 0 && !hasAutoSelectedRef.current) {
          hasAutoSelectedRef.current = true;
          const mostRecent = convs[0];
          try {
            const conv = await getConversation(authToken, mostRecent.id);
            const msgs: LocalMessage[] = conv.messages || [];
            setCurrentConversationId(mostRecent.id);
            if (hasDanglingUserMessage(msgs)) {
              setMessages([...msgs, { role: 'assistant', text: '', created_at: new Date().toISOString(), loading: true }]);
              setPollingConvId(mostRecent.id);
            } else {
              setMessages(msgs);
            }
          } catch (innerErr) {
            console.error('Failed to auto-load conversation:', innerErr);
          }
        }
      } catch (err) {
        console.error('Failed to load conversations:', err);
      } finally {
        setConversationsLoading(false);
      }
    };
    loadConversations();
  }, [authToken]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Poll for assistant response when a dangling user message is detected
  useEffect(() => {
    if (!pollingConvId || !authToken) return;

    let attempts = 0;
    const maxAttempts = 20; // 20 × 3s = 60s
    const intervalId = setInterval(async () => {
      attempts++;
      try {
        const conv = await getConversation(authToken, pollingConvId);
        const msgs = conv.messages || [];
        if (!hasDanglingUserMessage(msgs)) {
          // Assistant response arrived
          setMessages(msgs);
          setPollingConvId(null);
          clearInterval(intervalId);
          return;
        }
      } catch { /* ignore fetch errors during poll */ }

      if (attempts >= maxAttempts) {
        // Timeout — remove loading placeholder, user can regenerate
        setMessages(prev => prev.filter(m => !m.loading));
        setPollingConvId(null);
        clearInterval(intervalId);
      }
    }, 3000);

    return () => clearInterval(intervalId);
  }, [pollingConvId, authToken]);

  // Select a conversation and load its messages
  const handleSelectConversation = async (id: number) => {
    if (!authToken) return;
    setPollingConvId(null); // stop any active poll from previous conversation
    try {
      const conv = await getConversation(authToken, id);
      const msgs: LocalMessage[] = conv.messages || [];
      setCurrentConversationId(id);
      if (hasDanglingUserMessage(msgs)) {
        setMessages([...msgs, { role: 'assistant', text: '', created_at: new Date().toISOString(), loading: true }]);
        setPollingConvId(id);
      } else {
        setMessages(msgs);
      }
      conversationCreatingRef.current = null;
      setSidebarOpen(false);
    } catch (err) {
      console.error('Failed to load conversation:', err);
    }
  };

  // Start a new chat
  const handleNewChat = () => {
    setCurrentConversationId(null);
    setMessages([]);
    setSelectedCompanies([]);
    setPollingConvId(null);
    conversationCreatingRef.current = null;
    setSidebarOpen(false);
  };

  // Delete a conversation
  const handleDeleteConversation = async (id: number) => {
    if (!authToken) return;
    try {
      await deleteConversation(authToken, id);
      setConversations(conversations.filter(c => c.id !== id));
      if (currentConversationId === id) {
        handleNewChat();
      }
    } catch (err) {
      console.error('Failed to delete conversation:', err);
    }
  };

  // Send a message (non-blocking, supports concurrent queries)
  const handleSend = useCallback((text: string, options: ChatOptions) => {
    if (!text.trim()) return;

    // Pre-flight query limit check for free users
    if (subscriptionTier === 'free' && authToken) {
      checkQueryLimit(authToken).catch((err: any) => {
        if (err?.response?.status === 429) {
          setQueryLimitHit(true);
          return;
        }
      });
    }

    const pendingId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const controller = new AbortController();
    abortControllersRef.current.set(pendingId, controller);

    // Add user message + placeholder immediately
    const userMessage: LocalMessage = {
      role: 'user',
      text: text,
      created_at: new Date().toISOString(),
    };

    const placeholderMessage: LocalMessage = {
      role: 'assistant',
      text: '',
      created_at: new Date().toISOString(),
      pendingId: pendingId,
      loading: true,
    };

    setMessages(prev => [...prev, userMessage, placeholderMessage]);
    setPendingMessageIds(prev => new Set(prev).add(pendingId));

    // Fire async work (non-blocking)
    (async () => {
      try {
        // Create conversation if needed
        let conversationId = currentConversationId;
        if (!conversationId && authToken) {
          if (!conversationCreatingRef.current) {
            const title = text.length > 50 ? text.substring(0, 50) + '...' : text;
            conversationCreatingRef.current = createConversation(authToken, title).then(conv => {
              setCurrentConversationId(conv.id);
              setConversations(prev => [{ ...conv, title }, ...prev]);
              return conv.id;
            });
          }
          conversationId = await conversationCreatingRef.current;
          setCurrentConversationId(conversationId);
        }

        // Save user message to backend
        if (conversationId && authToken) {
          await addMessage(authToken, conversationId, 'user', text);
        }

        // Query the research API
        const params: ResearchQuery = {
          query: text,
          top_k: options.topK,
          deep_search: options.deepSearch,
          return_pdf: false,
          enable_multi_agent: options.multiAgent,
          language: options.language || 'en-IN',
          model: options.model || 'fast',
        };

        if (selectedCompanies.length > 0) {
          params.company_names = selectedCompanies;
        }

        const result: ResearchResult = await queryStockResearch(params, controller.signal);

        // Create assistant message with sources
        const sources: Source[] = result.sources?.map(s => ({
          type: s.type,
          title: s.title || s.company || 'Source',
          company: s.company,
          url: s.url,
          similarity: s.similarity,
          report_type: s.report_type,
        })) || [];

        const assistantMessage: LocalMessage = {
          role: 'assistant',
          text: result.llm_response || 'Sorry, I could not generate a response.',
          created_at: new Date().toISOString(),
          sources: sources,
          evaluation_trace: result.evaluation_trace,
          llm_response_en: result.llm_response_en,
          response_language: result.response_language,
        };

        // Save assistant message to backend and capture DB id
        let savedAssistantId: number | undefined;
        if (conversationId && authToken) {
          const saved = await addMessage(authToken, conversationId, 'assistant', assistantMessage.text);
          savedAssistantId = saved.message_id;
        }

        // Replace placeholder with real response (including DB id for feedback)
        setMessages(prev => prev.map(msg =>
          msg.pendingId === pendingId
            ? { ...assistantMessage, id: savedAssistantId, pendingId: undefined, loading: false }
            : msg
        ));

        // Decrement local query counter for free-tier users
        if (subscriptionTier === 'free') {
          dispatch(decrementQueriesRemaining());
        }
      } catch (err: any) {
        // Don't replace with error if it was an intentional abort
        if (err.name === 'AbortError') return;

        console.error('Failed to send message:', err);
        const errorMessage: LocalMessage = {
          role: 'assistant',
          text: `Error: ${err.message || 'Failed to get response'}`,
          created_at: new Date().toISOString(),
        };

        // Replace placeholder with error message
        setMessages(prev => prev.map(msg =>
          msg.pendingId === pendingId
            ? { ...errorMessage, pendingId: undefined, loading: false }
            : msg
        ));
      } finally {
        abortControllersRef.current.delete(pendingId);
        setPendingMessageIds(prev => {
          const next = new Set(prev);
          next.delete(pendingId);
          return next;
        });
      }
    })();
  }, [authToken, currentConversationId, selectedCompanies, subscriptionTier, dispatch]);

  // Stop a pending response
  const handleStop = useCallback((pendingId: string) => {
    const controller = abortControllersRef.current.get(pendingId);
    if (controller) {
      controller.abort();
      abortControllersRef.current.delete(pendingId);
    }

    // Replace placeholder with stopped message
    setMessages(prev => prev.map(msg =>
      msg.pendingId === pendingId
        ? { ...msg, text: 'Response stopped by user.', loading: false, pendingId: undefined }
        : msg
    ));

    setPendingMessageIds(prev => {
      const next = new Set(prev);
      next.delete(pendingId);
      return next;
    });
  }, []);

  // Edit a user message — truncate conversation and re-send
  const handleEditMessage = useCallback((messageIndex: number) => {
    setEditingMessageIndex(messageIndex);
  }, []);

  // Cancel editing
  const handleEditCancel = useCallback(() => {
    setEditingMessageIndex(null);
  }, []);

  // Called from ChatInput when the edited text is submitted
  const handleEditSend = useCallback((text: string, options: ChatOptions) => {
    if (editingMessageIndex != null) {
      // Truncate messages up to (not including) the edited message
      setMessages(prev => prev.slice(0, editingMessageIndex));
      setEditingMessageIndex(null);
    }
    handleSend(text, options);
  }, [editingMessageIndex, handleSend]);

  // Regenerate the last assistant response
  const handleRegenerate = useCallback(() => {
    // Find the last assistant message index
    let lastAssistantIdx = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant' && !messages[i].loading) {
        lastAssistantIdx = i;
        break;
      }
    }
    if (lastAssistantIdx < 0) return;

    // Find the user message right before it
    let userMsgIdx = -1;
    for (let i = lastAssistantIdx - 1; i >= 0; i--) {
      if (messages[i].role === 'user') {
        userMsgIdx = i;
        break;
      }
    }
    if (userMsgIdx < 0) return;

    const userText = messages[userMsgIdx].text;

    // Remove the last assistant message
    setMessages(prev => prev.slice(0, lastAssistantIdx));

    // Re-send with the same text
    handleSend(userText, { deepSearch: false, multiAgent: true, topK: 5, language: copilotLanguage, model: 'fast' });
  }, [messages, handleSend, copilotLanguage]);

  // Submit feedback for an assistant message
  const handleFeedback = useCallback(async (messageId: number, rating: number, comment: string) => {
    if (!authToken) return;
    await submitFeedback(authToken, messageId, rating, comment || undefined);
    setFeedbackMap(prev => ({ ...prev, [messageId]: { rating, comment } }));
  }, [authToken]);

  // ── Auto-trigger from ?q= search param ──
  useEffect(() => {
    if (conversationsLoading || qProcessedRef.current) return;
    const q = searchParams.get('q');
    if (q) {
      qProcessedRef.current = true;
      setSearchParams({}, { replace: true });
      // Start fresh chat
      setCurrentConversationId(null);
      setMessages([]);
      setSelectedCompanies([]);
      setPollingConvId(null);
      conversationCreatingRef.current = null;
      // Send after state settles
      setTimeout(() => {
        handleSend(q, { deepSearch: false, multiAgent: true, topK: 5, language: copilotLanguage, model: 'fast' });
      }, 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationsLoading]);

  return (
    <div
      className="flex h-[calc(100vh-3.5rem)] lg:h-screen"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      <SEO
        title="AI Copilot - Stock Research Assistant"
        description="Screen stocks by financial metrics, analyze companies with annual report data, compare peers, research sectors, and discover undervalued opportunities. Yantrai's AI Copilot searches concalls and news for research-grade answers."
        canonical="/copilot"
      />
      {/* Sidebar */}
      <ChatSidebar
        conversations={conversations}
        currentConversationId={currentConversationId}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onSelectConversation={handleSelectConversation}
        onNewChat={handleNewChat}
        onDeleteConversation={handleDeleteConversation}
        colors={colors}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div
          className="flex items-center justify-between p-4"
          style={{
            backgroundColor: colors.inputBackground,
            borderBottom: `1px solid ${colors.borderColor}`,
          }}
        >
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-lg lg:hidden"
              style={{ backgroundColor: colors.backgroundColor }}
            >
              {React.createElement(IoMenu as any, { size: 24, color: colors.textColor })}
            </button>
            <div className="flex items-center gap-2">
              {React.createElement(IoSparkles as any, { size: 24, color: colors.primary })}
              <CText type="B18" color={colors.textColor}>
                Research Copilot
              </CText>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {subscriptionTier === 'free' && queriesRemaining >= 0 && (
              <span
                className="text-xs px-2 py-1 rounded-full"
                style={{
                  backgroundColor: queriesRemaining <= 1 ? (colors.alertColor || '#E82C81') + '20' : colors.primary + '15',
                  color: queriesRemaining <= 1 ? (colors.alertColor || '#E82C81') : colors.primary,
                }}
              >
                {queriesRemaining}/5 queries left
              </span>
            )}
            <button
              onClick={handleNewChat}
              className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
              style={{ backgroundColor: colors.primary, color: colors.white }}
            >
              {React.createElement(IoAdd as any, { size: 18 })}
              <span className="hidden sm:inline text-sm font-medium">New Chat</span>
            </button>
          </div>
        </div>

        {/* Query Limit Modal */}
        {queryLimitHit && (
          <div
            className="mx-4 mt-2 p-4 rounded-xl flex items-center justify-between"
            style={{
              backgroundColor: (colors.alertColor || '#E82C81') + '10',
              border: `1px solid ${(colors.alertColor || '#E82C81')}30`,
            }}
          >
            <div>
              <CText type="M14" color={colors.textColor}>
                Weekly query limit reached
              </CText>
              <CText type="r12" color={colors.grayScale500}>
                Free plan allows 5 queries/week. Upgrade for unlimited access.
              </CText>
            </div>
            <button
              onClick={() => navigate('/pricing')}
              className="px-4 py-2 rounded-lg text-sm font-semibold flex-shrink-0 ml-3"
              style={{ backgroundColor: colors.primary, color: colors.white }}
            >
              Upgrade
            </button>
          </div>
        )}

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4">
          {conversationsLoading ? (
            <div className="flex items-center justify-center h-full">
              <CLoader inline />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center max-w-2xl mx-auto px-4">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                style={{ backgroundColor: colors.primary + '20' }}
              >
                {React.createElement(IoSparkles as any, { size: 32, color: colors.primary })}
              </div>
              <CText type="B20" color={colors.textColor}>
                Welcome to Research Copilot
              </CText>
              <CText type="r14" color={colors.grayScale500} style={{ marginTop: 8, maxWidth: 440 }}>
                Ask about stocks, sectors, and markets. I search annual reports, concalls & news.
              </CText>

              {/* Capability Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mt-6 w-full">
                {[
                  { icon: '🔍', title: 'Screen Stocks', query: 'Companies with ROCE > 20%, low debt, and growing revenue' },
                  { icon: '📊', title: 'Analyze a Company', query: "Deep-dive into HDFC Bank's fundamentals and recent performance" },
                  { icon: '🏭', title: 'Research a Sector', query: 'IT sector outlook and top picks for the next year' },
                  { icon: '⚖️', title: 'Compare Companies', query: 'Compare TCS vs Infosys vs Wipro on all financial metrics' },
                  { icon: '💎', title: 'Find Hidden Gems', query: 'Undervalued small-caps with strong moats and high promoter holding' },
                  { icon: '📰', title: 'Latest News & Events', query: "What's moving the Indian stock market today? Any key events?" },
                ].map((card) => (
                  <button
                    key={card.title}
                    onClick={() => handleSend(card.query, { deepSearch: false, multiAgent: true, topK: 5, language: copilotLanguage, model: 'fast' })}
                    className="p-4 rounded-xl text-left transition-all hover:-translate-y-0.5 hover:shadow-lg"
                    style={{
                      backgroundColor: colors.inputBackground,
                      border: `1px solid ${colors.borderColor}`,
                    }}
                  >
                    <div className="text-2xl mb-2">{card.icon}</div>
                    <CText type="M14" color={colors.textColor} style={{ display: 'block', fontWeight: 600 }}>
                      {card.title}
                    </CText>
                    <CText type="r12" color={colors.grayScale500} style={{ display: 'block', marginTop: 4, lineHeight: '1.4' }}>
                      &ldquo;{card.query.length > 55 ? card.query.slice(0, 52) + '...' : card.query}&rdquo;
                    </CText>
                  </button>
                ))}
              </div>

              {/* Quick-fire chips */}
              <div className="flex flex-wrap justify-center gap-2 mt-5">
                {[
                  'High ROCE + Growth stocks',
                  'Banking sector analysis',
                  'Small-cap hidden gems',
                  'Best dividend stocks',
                ].map((chip) => (
                  <button
                    key={chip}
                    onClick={() => handleSend(chip, { deepSearch: false, multiAgent: true, topK: 5, language: copilotLanguage, model: 'fast' })}
                    className="px-3 py-1.5 rounded-full text-xs transition-colors"
                    style={{
                      backgroundColor: colors.primary + '15',
                      border: `1px solid ${colors.primary}30`,
                      color: colors.primary,
                    }}
                  >
                    {chip}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto">
              {messages.map((message, idx) => {
                // Determine if this is the last non-loading assistant message
                const isLastAssistant = !message.loading
                  && message.role === 'assistant'
                  && (() => {
                    for (let i = messages.length - 1; i >= 0; i--) {
                      if (messages[i].role === 'assistant' && !messages[i].loading) return i === idx;
                    }
                    return false;
                  })();

                return message.loading ? (
                  <div key={idx} className="flex items-center gap-3 mb-4">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: colors.grayScale400 }}
                    >
                      {React.createElement(IoSparkles as any, { size: 16, color: colors.white })}
                    </div>
                    <div
                      className="p-4 rounded-2xl rounded-tl-sm"
                      style={{
                        backgroundColor: colors.inputBackground,
                        border: `1px solid ${colors.borderColor}`,
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <CLoader inline />
                        <CText type="r14" color={colors.grayScale500}>
                          {message.pendingId ? 'Researching...' : 'Waiting for response...'}
                        </CText>
                        {message.pendingId && (
                          <button
                            onClick={() => handleStop(message.pendingId!)}
                            className="flex items-center gap-1 ml-3 px-3 py-1 rounded-lg transition-colors"
                            style={{
                              backgroundColor: colors.backgroundColor,
                              border: `1px solid ${colors.borderColor}`,
                            }}
                            title="Stop generating"
                          >
                            {React.createElement(IoStop as any, { size: 14, color: colors.grayScale500 })}
                            <CText type="r12" color={colors.grayScale500}>
                              Stop
                            </CText>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <ChatMessage
                    key={idx}
                    message={message}
                    colors={colors}
                    isLastAssistant={isLastAssistant}
                    onEdit={message.role === 'user' ? () => handleEditMessage(idx) : undefined}
                    onRegenerate={isLastAssistant ? handleRegenerate : undefined}
                    llmResponseEn={message.llm_response_en}
                    responseLanguage={message.response_language}
                    feedbackRating={message.id ? feedbackMap[message.id]?.rating : undefined}
                    feedbackComment={message.id ? feedbackMap[message.id]?.comment : undefined}
                    onFeedback={handleFeedback}
                  />
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input Area */}
        <ChatInput
          onSend={editingMessageIndex != null ? handleEditSend : handleSend}
          isLoading={loading}
          selectedCompanies={selectedCompanies}
          onCompanySelect={setSelectedCompanies}
          allCompanies={allCompanies}
          colors={colors}
          editText={editingMessageIndex != null ? messages[editingMessageIndex]?.text : null}
          onEditCancel={handleEditCancel}
          deepSearchAllowed={deepSearchGate.allowed}
          language={copilotLanguage}
          onLanguageChange={handleLanguageChange}
          modelOptions={modelOptions}
        />
      </div>
    </div>
  );
};

export default Copilot;
